import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import Notification, { showToast } from '../../../components/common/notification/Notification';
import Spinner from '../../../components/common/spinner/Spinner';
import RowList from '../../components/listing/RowList';
import { Row, Col } from 'react-bootstrap';
import Pagination from '../../components/pagination/Pagination';
import PostFilter from './PostFilter';
import { Link } from "react-router-dom";
import "./filter.css";
import { FiSearch, FiPlus } from "react-icons/fi";
import SaaSEmptyState from "../../../components/common/saas/SaaSEmptyState";
import SaaSLoadingSkeleton from "../../../components/common/saas/SaaSLoadingSkeleton";
import "../../../components/common/saas/SaaSUIToolkit.css";
import "../propertyNew/PropertyListComponent.css";

const PostListComponent = ({route}) => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const assetUrl = process.env.REACT_APP_ASSET_URL;
  const token = loggedInUser?.refreshToken; 
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); 
  const [filteredPosts, setFilteredPosts] = useState([]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${baseUrl}/property/leads`,
        {
          page_number: currentPage,
          page_size: 20,
          order_by: "ABC",         
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log('hii', response.data); 

      if (response.status === 200 && response.data.statusType === 'SUCCESS') {
        setPosts(response.data.leads || []); 
        setTotalPages(response.data.totalPage || 1);
        console.log("posts", posts);
      } else {
        showToast('Failed to fetch Post data', { type: 'error' });
      } 
    } catch (error) {
      console.error('Fetch error:', error);
      showToast('Error fetching Post data', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (itemId) => {
    try {
      const response = await axios.delete(
        `${baseUrl}/property/lead/delete/${itemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.statusType === 'SUCCESS') {
        setPosts(posts.filter((item) => item.id !== itemId));
        showToast('Post deleted successfully', { type: 'success' });
      } else {
        showToast(`${response.data.text}`, { type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting Post :', error);
      showToast('Error occurred during deletion', { type: 'error' });
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    const filtered = posts.filter(post => {
      const customerName = post.customer?.first_name?.toLowerCase() || '';
      const partnerName = post.partner?.first_name?.toLowerCase() || '';
      const description = (post.description || '').toLowerCase();
      const city = (post.city_name || '').toLowerCase();
      const state = (post.state_name || '').toLowerCase();
      const country = (post.country_name || '').toLowerCase();

      return (
        customerName.includes(term) ||
        partnerName.includes(term) ||
        description.includes(term) ||
        city.includes(term) ||
        state.includes(term) ||
        country.includes(term)
      );
    });

    setFilteredPosts(filtered);
  };

  const applyCustomFilters = (filters) => {
  const filtered = posts.filter(post => {
    return (
      (!filters.state || post.state_name === filters.state) &&
      (!filters.city || post.city_name === filters.city) &&
      (!filters.service || post.service === filters.service) &&
      (!filters.propertyType || post.property_type === filters.propertyType)
    );
  });

  setFilteredPosts(filtered);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (  
    <div className="saas-post-list-container">
      <Notification />
      
      {/* SaaS Toolbar */}
      <div className="saas-toolbar mb-3">
        <div className="saas-search-input-wrap">
          <FiSearch className="saas-search-icon" size={16} />
          <input
            className="saas-search-input"
            type="text"
            placeholder="Search by name, city, state, country, service..."
            value={searchTerm}
            onChange={handleSearch}
          />
          {searchTerm && (
            <button
              type="button"
              className="saas-search-clear"
              onClick={() => {
                setSearchTerm('');
                setFilteredPosts([]);
              }}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="d-flex align-items-center gap-2">
          <PostFilter posts={posts} onApply={applyCustomFilters} />
          <Link to="/post/add-post" className="saas-btn saas-btn--primary">
            <FiPlus size={16} />
            <span>Create Post</span>
          </Link>
        </div>
      </div>

      {/* Results Meta Bar */}
      <div className="d-flex align-items-center justify-content-between mb-3 px-1">
        <span className="text-muted" style={{ fontSize: "0.8125rem", fontWeight: 600 }}>
          {isLoading ? "Fetching posts..." : `Showing ${((filteredPosts.length > 0 || searchTerm) ? filteredPosts : posts).length} Results`}
        </span>
        {searchTerm && (
          <span className="badge bg-light text-secondary" style={{ fontSize: "0.75rem" }}>
            Filtered by: "{searchTerm}"
          </span>
        )}
      </div>

      {/* Content List */}
      <div>
        {isLoading ? (
          <SaaSLoadingSkeleton type="list" count={4} />
        ) : (
          (() => {
            const displayPosts = (filteredPosts.length > 0 || searchTerm) ? filteredPosts : posts;
            if (displayPosts.length === 0) {
              return (
                <SaaSEmptyState
                  icon={<FiSearch />}
                  title="No Posts Found"
                  description="We couldn't find any community posts matching your search or filters. Try resetting your search or create a new post."
                  actionLabel="Create New Post"
                  actionLink="/post/add-post"
                />
              );
            }
            return displayPosts.map((item) => (
              <RowList
                key={item.id}
                title={item.service}
                creator={`${item.customer?.first_name || ''} ${item.customer?.last_name || ''}`}
                CardImg={item.images?.[0]?.image_url ? assetUrl + (item.images?.[0]?.image_url ) : null}
                createOn={item.created_on} 
                item={item}
                route={route}
                handleDelete={handleDelete}
              /> 
            )); 
          })()
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 mb-4 d-flex justify-content-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  ); 
};

export default PostListComponent;
 